<template>
    <div class="ma-4">
        <v-snackbar
            v-model="msg"
            top
            :color="snackbarColor"
            :timeout="3000"
            elevation="4"
            class="rounded-lg"
        >
            <div class="d-flex align-center">
                <v-icon class="mr-2">{{
                    snackbarColor === "success" ? "mdi-check-circle" : "mdi-alert"
                }}</v-icon>
                {{ text }}
            </div>
            <template v-slot:action="{ attrs }">
                <v-btn icon small v-bind="attrs" @click="msg = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </template>
        </v-snackbar>

        <v-card class="rounded-lg">
            <v-app-bar flat dense color="grey lighten-4" class="rounded-t-lg px-3">
                <v-btn icon small class="mr-2 grey lighten-3" @click="$router.go(-1)">
                    <v-icon>navigate_before</v-icon>
                </v-btn>

                <span class="text-h6 font-weight-medium grey--text text--darken-3">
                    Medical Visit Report
                </span>

                <v-spacer></v-spacer>

                <v-btn
                    fab
                    small
                    @click="printTable()"
                    class="mr-2 white--text"
                    color="primary"
                    elevation="2"
                >
                    <v-icon>print</v-icon>
                </v-btn>
            </v-app-bar>

            <v-divider></v-divider>

            <v-card-text class="pa-6 grey lighten-5">
                <v-row dense>
                    <v-col cols="12" md="2">
                        <v-menu
                            ref="calendarMenu"
                            v-model="calendarMenu"
                            :close-on-content-click="false"
                            :return-value.sync="date"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="reportFilters.startDate"
                                    label="Start Date"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                    :rules='requiredField'
                                    clearable
                                    outlined
                                    dense
                                    hide-details
                                    class="rounded-lg"
                                    background-color="white"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="reportFilters.startDate"
                                no-title
                                scrollable
                                :rules='requiredField'
                                clearable
                                :max="reportFilters.endDate"
                                class="rounded-lg"
                            >
                                <v-spacer></v-spacer>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="calendarMenu = false"
                                >
                                    Cancel
                                </v-btn>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="$refs.calendarMenu.save(date)"
                                >
                                    OK
                                </v-btn>
                            </v-date-picker>
                        </v-menu>
                    </v-col>

                    <v-col cols="12" md="2">
                        <v-menu
                            ref="calendar2Menu"
                            v-model="calendar2Menu"
                            :close-on-content-click="false"
                            :return-value.sync="date"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="reportFilters.endDate"
                                    label="End Date"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                    :rules='requiredField'
                                    clearable
                                    outlined
                                    dense
                                    hide-details
                                    class="rounded-lg"
                                    background-color="white"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="reportFilters.endDate"
                                no-title
                                scrollable
                                :rules='requiredField'
                                clearable
                                class="rounded-lg"
                            >
                                <v-spacer></v-spacer>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="calendar2Menu = false"
                                >
                                    Cancel
                                </v-btn>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="$refs.calendar2Menu.save(date)"
                                >
                                    OK
                                </v-btn>
                            </v-date-picker>
                        </v-menu>
                    </v-col>

                    <v-col cols="12" md="2">
                        <v-select
                            label="Select a Program"
                            :items="programs"
                            item-text="Name"
                            item-value="Name"
                            v-model="reportFilters.program"
                            clearable
                            @change="visitQuery()"
                            outlined
                            dense
                            hide-details
                            class="rounded-lg"
                            background-color="white"
                        ></v-select>
                    </v-col>

                    <v-col cols="12" md="2">
                        <v-select
                            label="Client Status"
                            :items="clientStatuses"
                            v-model="reportFilters.clientStatus"
                            clearable
                            @change="visitQuery()"
                            outlined
                            dense
                            hide-details
                            class="rounded-lg"
                            background-color="white"
                        ></v-select>
                    </v-col>

                    <v-col cols="12" md="2">
                        <v-select
                            label="Select a Client"
                            :items="filteredClients"
                            item-text="Name"
                            item-value="._id"
                            v-model="reportFilters.ClientID"
                            clearable
                            @change="visitQuery()"
                            outlined
                            dense
                            hide-details
                            class="rounded-lg"
                            background-color="white"
                        ></v-select>
                    </v-col>

                    <v-col cols="12" md="2">
                        <v-btn
                            color="primary"
                            block
                            height="40"
                            class="rounded-lg"
                            :loading="isLoading"
                            elevation="1"
                            @click="visitQuery()"
                        >
                            <v-icon left>mdi-magnify</v-icon>
                            Search
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-text>

            <!-- Data Table -->
            <v-card-text class="pa-6">
                <v-data-table
                    :headers="headers"
                    :items="visits"
                    :server-items-length="itemCount"
                    :options.sync="options"
                    :loading="isLoading"
                    loading-text="Loading Medical Visits"
                    class="elevation-1 rounded-lg"
                    :items-per-page="10"
                    :page.sync="page"
                    :pageCount="pageCount"
                    @update:options="visitQuery"
                    :footer-props="{
                        'items-per-page-options': [10, 25, 50, 100],
                        showFirstLastPage: true,
                        'items-per-page-text': 'Rows per page:',
                        class: 'rounded-lg',
                    }"
                >
                    <template v-slot:item.select="{ item }">
                        <v-btn
                            x-small
                            color="primary"
                            @click="showEventForm(item)"
                            class="px-3"
                        >
                            <v-icon left x-small>mdi-eye</v-icon>
                            View
                        </v-btn>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <v-dialog v-model="dialog" max-width="80%" class="rounded-lg">
            <v-card class="rounded-lg">
                <v-card-title class="primary white--text">
                    <span class="text-h6">Viewing Medical Visit</span>
                </v-card-title>
                <v-card-text class="pt-6">
                    <v-container>
                        <v-form
                            ref="form"
                            lazy-validation>
                            <v-row>
                                <v-col cols="12" sm="12" md="6">
                                    <span>Annual Visit?</span>
                                    <v-row class="ml-0">
                                        <v-switch
                                            readonly
                                            label="Yes"
                                            value="Yes"
                                            v-model="item.annualVisit"
                                        ></v-switch>
                                        <v-switch
                                            readonly
                                            label="No"
                                            value="No"
                                            v-model="item.annualVisit"
                                        ></v-switch>
                                    </v-row>
                                </v-col>
                                <v-col cols="12" sm="12" md="6">
                                    <v-text-field
                                        readonly
                                        v-model="item.date"
                                        label="Date and Time of Visit"
                                        prepend-icon="mdi-calendar"
                                        outlined
                                        dense
                                        class="rounded-lg"
                                    >
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" sm="12" md="4">
                                    <v-text-field
                                        readonly
                                        v-model="item.visitType"
                                        label="Visit Type"
                                        outlined
                                        dense
                                        class="rounded-lg"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="12" md="4">
                                    <v-text-field
                                        readonly
                                        return-object
                                        label="Reviewing Provider"
                                        v-model="item.reviewingProvider.Name"
                                        outlined
                                        dense
                                        class="rounded-lg"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="6" md="4">
                                    <v-text-field
                                        readonly
                                        v-model="item.reviewingProvider.Profession"
                                        label="Provider Type"
                                        outlined
                                        dense
                                        class="rounded-lg"
                                    ></v-text-field>
                                </v-col>

                                <v-col cols="12" sm="12" md="6">
                                    <v-text-field
                                        readonly
                                        label="Facility Name"
                                        v-model="item.reviewingProvider.Company"
                                        outlined
                                        dense
                                        class="rounded-lg"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="12" md="6">
                                    <v-text-field
                                        readonly
                                        label="Facility Address"
                                        v-model="item.reviewingProvider.Address"
                                        outlined
                                        dense
                                        class="rounded-lg"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="12" md="6">
                                    <v-text-field 
                                        readonly 
                                        label="City"
                                        v-model="item.reviewingProvider.City"
                                        outlined
                                        dense
                                        class="rounded-lg"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="12" md="6">
                                    <v-text-field 
                                        readonly 
                                        label="State"
                                        v-model="item.reviewingProvider.State"
                                        outlined
                                        dense
                                        class="rounded-lg"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="12" md="6">
                                    <v-text-field 
                                        readonly 
                                        label="Zip"
                                        v-model="item.reviewingProvider.Zip"
                                        outlined
                                        dense
                                        class="rounded-lg"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="12" md="6">
                                    <v-text-field
                                        readonly
                                        label="Phone Number"
                                        v-model="item.reviewingProvider.Phone"
                                        outlined
                                        dense
                                        class="rounded-lg"
                                    ></v-text-field>
                                </v-col>

                                <v-col cols="12" sm="12" md="12">
                                    <v-textarea
                                        label="Reason for Visit"
                                        v-model="item.reason"
                                        outlined
                                        class="rounded-lg"
                                    ></v-textarea>
                                </v-col>

                                <v-col cols="12">
                                    <span>Were there any medication changes?</span>
                                    <v-switch
                                        readonly
                                        label="Yes"
                                        value="Yes"
                                        v-model="item.medChange"
                                    ></v-switch>
                                    <v-switch
                                        readonly
                                        label="No"
                                        value="No"
                                        v-model="item.medChange"
                                    ></v-switch>
                                </v-col>

                                <v-col cols="12">
                                    <v-textarea
                                        readonly
                                        label="What medication(s) was changed?"
                                        outlined
                                        v-model="item.medChangeDescription"
                                        class="rounded-lg"
                                    ></v-textarea>
                                </v-col>

                                <v-col cols="12">
                                    <div class="blue darken-4 white--text pa-4">
                                        THIS SECTION SHOULD BE FILLED OUT BY THE STAFF
                                    </div>
                                </v-col>
                                <v-col cols="12" sm="12">
                                    <v-expansion-panels
                                        v-model="panel"
                                        multiple
                                    >
                                        <v-expansion-panel>
                                            <v-expansion-panel-header disable-icon-rotate
                                                                      class="text-uppercase font-weight-bold font-italic info">
                                                Document List
                                                <template v-slot:actions>
                                                    <v-icon>
                                                        mdi-view-list
                                                    </v-icon>
                                                </template>
                                            </v-expansion-panel-header>

                                            <v-expansion-panel-content>
                                                <template>
                                                    <v-card
                                                        class="mx-auto"
                                                        tile
                                                    >
                                                        <v-list dense>
                                                            <v-list-item-group
                                                                color="primary"
                                                            >
                                                                <v-list-item
                                                                    v-for="(item, i) in uploadedDocuments"
                                                                    :key="i"
                                                                >
                                                                    <v-list-item-content>
                                                                        <v-chip
                                                                            class=""
                                                                            color="primary"
                                                                            text-color=""
                                                                            label
                                                                        >
                                                                            {{ item.name }}
                                                                        </v-chip>
                                                                        <div v-if="item.ext !=='pdf' ">
                                                                            <v-img
                                                                                max-height="280"
                                                                                max-width="350"
                                                                                :src="item.aws"
                                                                            ></v-img>
                                                                        </div>
                                                                        <div v-if="item.ext ==='pdf' ">
                                                                            <load-pdf
                                                                                :pdfFile="item.aws"
                                                                                :id="item.id"
                                                                            />
                                                                        </div>
                                                                    </v-list-item-content>
                                                                </v-list-item>
                                                            </v-list-item-group>
                                                        </v-list>
                                                    </v-card>
                                                </template>

                                            </v-expansion-panel-content>
                                        </v-expansion-panel>
                                    </v-expansion-panels>
                                </v-col>
                                <v-col cols="12" sm="12">
                                    <v-col cols="12" sm="12" md="3"
                                           v-for="item in newAdditionalDocuments"
                                           :key="item.aws"
                                    >
                                    </v-col>
                                    <v-expansion-panels
                                        v-model="panel2"
                                        multiple
                                    >
                                        <v-expansion-panel>
                                            <v-expansion-panel-header disable-icon-rotate
                                                                      class="text-uppercase font-weight-bold font-italic info">
                                                Additional Documents List
                                                <template v-slot:actions>
                                                    <v-icon>
                                                        mdi-view-list
                                                    </v-icon>
                                                </template>
                                            </v-expansion-panel-header>
                                            <v-expansion-panel-content>
                                                <template>
                                                    <v-card
                                                        class="mx-auto"
                                                        tile
                                                    >
                                                        <v-list dense>
                                                            <v-list-item-group
                                                                color="primary"
                                                            >
                                                                <v-list-item
                                                                    v-for="(item, i) in uploadedAdditionalDocuments"
                                                                    :key="i"
                                                                >
                                                                    <v-list-item-content>
                                                                        <v-chip
                                                                            class=""
                                                                            color="primary"
                                                                            text-color=""
                                                                            label
                                                                        >
                                                                            {{ item.name }}
                                                                        </v-chip>
                                                                        <div v-if="item.ext !=='pdf' ">
                                                                            <v-img
                                                                                max-height="280"
                                                                                max-width="350"
                                                                                :src="item.aws"
                                                                            ></v-img>
                                                                        </div>
                                                                        <div v-if="item.ext ==='pdf' ">
                                                                            <load-pdf
                                                                                :pdfFile="item.aws"
                                                                                :id="item.id"
                                                                            />
                                                                        </div>
                                                                    </v-list-item-content>
                                                                </v-list-item>
                                                            </v-list-item-group>
                                                        </v-list>
                                                    </v-card>
                                                </template>
                                            </v-expansion-panel-content>
                                        </v-expansion-panel>
                                    </v-expansion-panels>
                                </v-col>
                                <v-col cols="12" sm="12" md="6">
                                    <v-text-field
                                        readonly
                                        label="Current Weight"
                                        v-model="item.weight"
                                        outlined
                                        dense
                                        class="rounded-lg"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="12" md="6">
                                    <v-text-field
                                        readonly
                                        label="Current Blood Pressure"
                                        v-model="item.bloodPressure"
                                        outlined
                                        dense
                                        class="rounded-lg"
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="12">
                                    <v-textarea
                                        readonly
                                        label="Comments on Visit"
                                        outlined
                                        v-model="item.comments"
                                        :rules="requiredField"
                                        class="rounded-lg"
                                    ></v-textarea>
                                </v-col>
                                <v-col cols="12" sm="12" md="12">
                                    <v-checkbox
                                        readonly
                                        label="Follow Up Appointment Needed"
                                        v-model="item.followUp"
                                        @click="showEventDialog(item.followUp)"
                                    ></v-checkbox>
                                </v-col>

                                <v-row class="ma-0">
                                    <v-col cols="12" sm="12" md="6">
                                        <v-text-field
                                            readonly
                                            v-model="item.followUpDate"
                                            label="Date and Time of Follow up Visit"
                                            prepend-icon="mdi-calendar"
                                            v-if="item.followUp"
                                            outlined
                                            dense
                                            class="rounded-lg"
                                        >
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="12" md="6" v-if="item.followUp">
                                        <v-text-field
                                            readonly
                                            v-model="item.followUpDate"
                                            label="Date and Time of Visit"
                                            prepend-icon="mdi-calendar"
                                            outlined
                                            dense
                                            class="rounded-lg"
                                        >
                                        </v-text-field>
                                    </v-col>
                                </v-row>

                                <v-col cols="12" sm="12" md="12">
                                    <v-text-field
                                        v-model="item.signature"
                                        readonly
                                        outlined
                                        dense
                                        class="rounded-lg"
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-container>
                </v-card-text>

                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn 
                        color="primary" 
                        @click="dialog = false"
                        elevation="1"
                    >
                        Back
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- <add-events-dialog
          :newEvent="newEvent"
          @on-add-event="(item) => onAddEvent(item)"
          @onShowEventDialog="(item) => showEventDialog(item)"
        /> -->

        <div id="print">
            <report-header>
              <h2>Medical Visit Report</h2>
            </report-header>
            <v-row>
                <v-col cols="12">
                    <v-data-table
                        :headers="headers"
                        :items="visits"
                        sort-by="Date"
                        sort-desc
                        class="elevation-1"
                        :loading="isLoading"
                        loading-text="Loading Medical Visits"
                        :items-per-page="-1"
                        @update:options="visitQuery"
                    >
                    </v-data-table>
                </v-col>
            </v-row>
        </div>
    </div>
</template>
<script>
import axios from 'axios'
// import Foot from '../components/Footer'
import {mapState, mapActions, mapGetters} from "vuex";
import LoadPdf from '../client/MedVisit/loadPDF.vue';
// import AddEventsDialog from '@/components/events/addEventsDialog.vue';
import renameFileHelper from '@/mixins/renameFileHelper';
import DateTimeUtil from "../../../utils/date-time.util";
import { STATUS_ARRAY } from '../../enums/UserStatusEnum';
import ReportHeader from '@/components/Report/ReportHeader.vue';
import { DateTime } from 'luxon';
import ReportFilters from '@/mixins/reportFilters.js';


const url = require("../../client_config/config").url;

export default {
    mixins: [renameFileHelper, ReportFilters],
    // components: { LoadPdf, AddEventsDialog },
    components: {
        LoadPdf,
        ReportHeader
    },
    props: {
        newItem: {
            type: Boolean,
            default: true,
        },
        staffName: {
            type: String,
            default: "",
        },
        clientName: {
            type: String,
            default: "",
        }
    },
    data: () => ({
        headers: [
            {text: "Client", align: 'start', value: "ClientInfo.Name", sortable: true},
            {
                text: 'Start Date',
                value: 'Date',
                sortable: true,
            },
            {text: "Visit Type", value: "VisitType", sortable: true},
            {text: "Reviewing Provider", value: "ReviewingProvider.Name", sortable: true},
            {text: "Annual Visit", value: "AnnualVisit", sortable: true},
            {text: "Signature", value: "Signature", sortable: true},
            {text: "Med Change", value: "MedChange", sortable: true},
            // { text: "State", value: "State" },
            {text: "", value: "select", sortable: false},

        ],
        items: [
            {text: 'Real-Time', icon: 'mdi-close-circle'},
            {text: 'Audience', icon: 'mdi-account'},
            {text: 'Conversions', icon: 'mdi-flag'},
        ],
        date: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
        calendarMenu: false,
        calendar2Menu: false,
        dialog: false,
        isLoading: false,
        titleForm: "New Med Visit",
        isDownloadPDF: false,
        isAdditionalPDF: false,
        isDownloadIMAGE: false,
        isAdditionalIMAGE: false,
        visits: [],
        clients: [],
        programs: [],
        providers: [],
        userState: localStorage.getItem('userState'),
        item: {
            annualVisit: "",
            visitType: "",
            reviewingProvider: "",
            providerType: "",
            reason: "",
            medChange: false,
            medChangeDescription: "",
            facility: "",
            address: "",
            city: "",
            state: "",
            zip: "",
            phone: "",
            date: "",
            document: "",
            additionalDocument: "",
            weight: "",
            bloodPressure: "",
            comments: "",
            followUp: "",
            followUpDate: "",
            signature: localStorage.getItem("Name") + " - " + new Date(),
            eventID: "",
        },

        defaultItem: {
            annualVisit: "",
            visitType: "",
            reviewingProvider: "",
            providerType: "",
            reason: "",
            medChange: false,
            medChangeDescription: "",
            facility: "",
            address: "",
            city: "",
            state: "",
            zip: "",
            phone: "",
            date: "",
            document: "",
            additionalDocument: "",
            weight: "",
            bloodPressure: "",
            comments: "",
            followUp: "",
            followUpDate: "",
            signature: localStorage.getItem("Name") + " - " + new Date(),
            eventID: "",
        },
        newVisit: false,
        signed: true,
        visitID: "",
        uploadFile: "",
        uploadFile2: "",
        newDocuments: [],
        uploadedDocuments: [],
        newAdditionalDocuments: [],
        documentPDF: [],
        isDocumentPDF: false,
        uploadedAdditionalDocuments: [],
        documentAdditionalPDF: [],
        requiredField: [
            v => !!v || 'This is required',
        ],
        panel: [0, 1],
        panel2: [0, 1],
        newEvent: true,
        fileCounter: 1,
        state: localStorage.getItem('userState'),
        page: 1,
        pageCount: 1,
        itemCount: 0,
        options: {},
        clientStatuses: STATUS_ARRAY,
        reportFilters:  {
            startDate: '',
            endDate: '',
            clientStatus: '',
            ClientID: '',
            program: ''
        },
        reportFilterName: 'ReportFilters_MedicalVisit',
        msg: false,
        text: "",
        snackbarColor: "success",
    }),

    computed: {
        ...mapState({
            staff: (state) => state.users.user.Name,
        }),
        ...mapGetters("timezones", ["getDefaultTz"]),
        filteredClients() {            
            const clientStatus = this.reportFilters.clientStatus;
            if (!(clientStatus == '' || clientStatus == null)) {
                return this.clients.filter((client) => client.Status == clientStatus);
            }
                
            return this.clients;
        },
        
        // For client-side sorting (alternative approach)
        sortedVisits() {
            if (!this.visits || this.visits.length === 0) return [];
            
            const { sortBy = ['Date'], sortDesc = [true] } = this.options;
            
            return [...this.visits].sort((a, b) => {
                const field = sortBy[0];
                const isDesc = sortDesc[0];
                
                let aVal = this.getNestedValue(a, field);
                let bVal = this.getNestedValue(b, field);
                
                // Handle date sorting
                if (field === 'Date') {
                    aVal = new Date(aVal);
                    bVal = new Date(bVal);
                }
                
                // Handle string sorting
                if (typeof aVal === 'string') {
                    aVal = aVal.toLowerCase();
                    bVal = bVal.toLowerCase();
                }
                
                if (aVal < bVal) return isDesc ? 1 : -1;
                if (aVal > bVal) return isDesc ? -1 : 1;
                return 0;
            });
        }
    },

    created() {
        this.getClients();
        this.getPrograms();
        this.visitQuery();
        this.reportFilters = this.getReportFilters(this.reportFilterName) ?? this.reportFilters;
    },    

    watch: {
      reportFilters: {
          handler(newVal) {
              this.setReportFilters(this.reportFilterName, newVal);
          },
          deep: true
      }
    },

    methods: {
        ...mapActions("medicalVisit", ["addItem"]),

        validate() {
            return this.$refs.form.validate();
        },
        validateReset() {
            if (this.$refs.form != undefined) {
                this.$refs.form.reset();
            }
        },

        showEventForm(item) {
            const id = item._id;
            const clientID = item.ClientID;

            this.$router.push({ path: `/medVisit/${clientID}/view-visit/${id}`,
                    query: {clientNav: 'false', 'newVisit': 'false'}
            });
        },

        async getClients() {
            let params = {};
            let endpoint = `${url}/clients`;

            const companyIds = localStorage.getItem('userCompanies');

            params['Company:in'] = JSON.parse(companyIds);
            params['State'] = this.userState;

            await axios.get(endpoint, {params})
                .then((res) => {
                    this.clients = res.data.clients;
                });
        },

        async getPrograms() {
            await axios.get(`${url}/units/state/${this.userState}`)
                .then((res) => {
                    this.programs = res.data.units;
                });
        },

        async visitQuery(options = {}) {

            this.visits = [];
            this.isLoading = true;

            const { page = 1, itemsPerPage = 10, sortBy = [], sortDesc = [] } = options;
            
            let sortField = 'Date';
            let sortDirection = -1;
            
            if (sortBy.length > 0) {
                sortField = sortBy[0];
                sortDirection = sortDesc[0] ? -1 : 1;
            }

            let today = DateTime.now();
            let lastMonth = DateTime.now().minus({months:1});

            let defaultEndDate = today.toFormat('yyyy-MM-dd');
            let defaultStartDate = lastMonth.toFormat('yyyy-MM-dd');
            let startDate = this.reportFilters.startDate || defaultStartDate;
            let endDate = this.reportFilters.endDate || defaultEndDate;

            const companyIds = localStorage.getItem("userCompanies");
            
            const params = {
                program: this.reportFilters.program || '',
                startDate: DateTimeUtil.toUTCISOString(DateTimeUtil.toStartOfDay(startDate)),
                endDate:  DateTimeUtil.toUTCISOString(DateTimeUtil.toEndOfDay(endDate)),
                clientId: this.reportFilters.ClientID || '',
                state: this.state || '',
                page: page,
                limit: itemsPerPage,
                clientStatus: this.reportFilters.clientStatus,
                sortField: sortField,
                sortDirection: sortDirection,
                Company: companyIds ? JSON.parse(companyIds) : null
            };

            // Remove empty parameters to avoid filtering issues
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) {
                    delete params[key];
                }
            });

            console.log('Medical Visits Query Params:', params);


            await axios.get(`${url}/medicalVisits/reports`, {params})
                .then(response => {
                    console.log('Medical Visits Response:', response.data);
                    const {docs, totalPages, totalDocs} = response.data;
                    this.pageCount = totalPages;
                    this.itemCount = totalDocs;

                    this.visits = docs.map(visits => {
                        if (visits.Date && visits.Date.includes('Z')) {
                            return {
                                ...visits,
                                Date: DateTimeUtil.toLocalDateTime(visits.Date)
                            };
                        } else {
                            return {
                                ...visits,
                                Date: visits.Date
                            };
                        }
                    });

                    this.isLoading = false;
                }).catch(error => {
                    console.error('Error fetching medical visits:', error);
                    console.error('Error response:', error.response?.data);
                    this.isLoading = false;
                });
        },

        convertKey(data) {
            const output = Object.entries(data).map(([key, value]) => [
                key[0].toLowerCase() + key.slice(1),
                value,
            ]);

            return Object.fromEntries(output);
        },

        close() {
            this.dialog = false;
            this.$nextTick(() => {
                this.item = Object.assign({}, this.defaultItem);
            });
            this.uploadedDocuments = [];
            this.uploadedAdditionalDocuments = [];
            this.newDocuments = [];
            this.newAdditionalDocuments = [];
            this.fileCounter = 1
        },
        showEventDialog(e) {
            this.newEvent = true;
            this.$store.commit("events/SET_DIALOG", e);
            this.item.followUp = e;
            console.log(e);
        },

        printTable() {
            // Set options for printing (all items, no pagination)
            const printOptions = {
                page: 1,
                itemsPerPage: -1,
                sortBy: this.options.sortBy || ['Date'],
                sortDesc: this.options.sortDesc || [true]
            };
            
            this.visitQuery(printOptions);
            window.print();
        },
        
        // Helper method for client-side sorting
        getNestedValue(obj, path) {
            return path.split('.').reduce((current, key) => {
                return current && current[key] !== undefined ? current[key] : '';
            }, obj);
        },
        
        // Alternative: Fetch all data for client-side sorting
        async fetchAllVisitsForSorting() {
            this.isLoading = true;
            
            let today = DateTime.now();
            let lastMonth = DateTime.now().minus({months:1});
            let defaultEndDate = today.toFormat('yyyy-MM-dd');
            let defaultStartDate = lastMonth.toFormat('yyyy-MM-dd');

            const companyIds = localStorage.getItem("userCompanies");
            
            const params = {
                program: this.reportFilters.program || '',
                startDate: this.reportFilters.startDate || defaultStartDate,
                endDate: this.reportFilters.endDate || defaultEndDate,
                clientId: this.reportFilters.ClientID || '',
                state: this.state || '',
                page: 1,
                limit: 10000, // Fetch all records
                clientStatus: this.reportFilters.clientStatus,
                Company: companyIds ? JSON.parse(companyIds) : null
            };

            try {
                const response = await axios.get(`${url}/medicalVisits/reports`, {params});
                const {docs} = response.data;
                
                this.visits = docs.map(visits => {
                    if (visits.Date && visits.Date.includes('Z')) {
                        return {
                            ...visits,
                            Date: DateTimeUtil.toLocalDateTime(visits.Date)
                        };
                    } else {
                        return {
                            ...visits,
                            Date: visits.Date
                        };
                    }
                });
                
                this.isLoading = false;
            } catch (error) {
                console.error('Error fetching all visits:', error);
                this.isLoading = false;
            }
        }
    }
}
</script>

<style scoped>
.v-data-table {
  border: 1px solid #e0e0e0;
}

.v-data-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.v-data-table ::v-deep tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.v-btn {
  text-transform: none;
  letter-spacing: 0;
}

.v-text-field ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.v-select ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.rounded-lg {
  border-radius: 8px !important;
}

.rounded-t-lg {
  border-top-left-radius: 8px !important;
  border-top-right-radius: 8px !important;
}
</style>

<style>
#print {
    display: none
}

@media only screen and (min-width: 768px) {
    .mobile {
        display: none;
    }
}

@media only screen and (max-width: 600px) {
    .desktop {
        display: none;
    }

}


@media only screen and (min-width: 768px) {
    .mobile {
        display: none;
    }
}

@media only screen and (max-width: 600px) {
    .desktop {
        display: none;
    }

}

@media print {
    #print {
        display: block;
        border: none;
        width: 1200px;
        padding-right: 300px;
        margin-left: -200px;
    }


    nav.v-navigation-drawer, header.v-toolbar, button.v-btn, div.v-card {
        display: none;
        border: none;

    }

    @page {
        margin: 0;
    }

    body {
        margin: 1.6cm;
    }


}
</style>